// src/admin/AdminUploadPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { auth, storage, db } from "../firebaseConfig";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  UploadTaskSnapshot,
} from "firebase/storage";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { onAuthStateChanged, type User } from "firebase/auth";
import "../styles/AdminUploadPage.scss";

type UploadedImage = { url: string; caption: string };

function slugify(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

const AdminUploadPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("Fashion");

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [captions, setCaptions] = useState<string[]>([]);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0); // 0..100

  // --- auth guard ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setCheckingAuth(false);
    });
    return () => unsub();
  }, []);

  // --- previews management + cleanup ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files);
    setFiles(selected);
    const urls = selected.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    setCaptions(new Array(selected.length).fill(""));
  };

  // revoke object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      previews.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [previews]);

  const handleCaptionChange = (index: number, value: string) => {
    setCaptions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const totalFiles = files.length;
  const progressLabel = useMemo(
    () => (uploading ? `Uploading… ${progress}%` : ""),
    [uploading, progress]
  );

  const handleUpload = async () => {
    if (!eventName || files.length === 0) return;

    setUploading(true);
    setProgress(0);

    try {
      const uploadedImages: UploadedImage[] = [];
      const base = slugify(eventName) || "event";
      let completed = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileRef = ref(
          storage,
          `events/${base}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`
        );
        const task = uploadBytesResumable(fileRef, file);

        await new Promise<void>((resolve, reject) => {
          task.on(
            "state_changed",
            (snap: UploadTaskSnapshot) => {
              // per-file percentage
              const percent = (snap.bytesTransferred / snap.totalBytes) * 100;

              // approximate aggregate progress across all files
              const perFileWeight = 100 / totalFiles; // each file contributes equally
              const aggregate =
                Math.floor(completed * perFileWeight + (percent / 100) * perFileWeight);

              setProgress(Math.min(100, aggregate));
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploadedImages.push({ url, caption: captions[i] || "" });
              completed += 1;
              // ensure we bump progress at file boundary
              const perFileWeight = 100 / totalFiles;
              setProgress(Math.min(100, Math.floor(completed * perFileWeight)));
              resolve();
            }
          );
        });
      }

      await addDoc(collection(db, "events"), {
        event: eventName,        // keep your original field name to avoid breaking other pages
        category,
        images: uploadedImages,  // [{ url, caption }]
        createdAt: Timestamp.now(),
      });

      // reset state
      setEventName("");
      setCategory("Fashion");
      setFiles([]);
      previews.forEach((u) => URL.revokeObjectURL(u));
      setPreviews([]);
      setCaptions([]);
      setProgress(100);
      alert("Upload complete!");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Failed to upload. Check console for details.");
    } finally {
      setUploading(false);
      // let the 100% be visible for a moment
      setTimeout(() => setProgress(0), 800);
    }
  };

  // ---- route gate ----
  if (checkingAuth) return <div>Loading...</div>;
  if (!user) return <Navigate to="/admin-login" replace />;

  return (
    <div className="admin-upload-container">
      <h2>Upload New Event</h2>

      <input
        type="text"
        placeholder="Event Name"
        value={eventName}
        onChange={(e) => setEventName(e.target.value)}
        disabled={uploading}
      />

      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="category-select"
        disabled={uploading}
      >
        <option value="Fashion">Fashion</option>
        <option value="Editorial">Editorial</option>
        <option value="Commercial">Commercial</option>
        <option value="Runway">Runway</option>
      </select>

      <input
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        disabled={uploading}
      />

      <div className="preview-grid">
        {previews.map((url, index) => (
          <div key={index} className="preview-item">
            <img src={url} alt={`Preview ${index + 1}`} />
            <input
              type="text"
              placeholder="Caption..."
              value={captions[index] || ""}
              onChange={(e) => handleCaptionChange(index, e.target.value)}
              disabled={uploading}
            />
          </div>
        ))}
      </div>

      {uploading && <p>{progressLabel}</p>}

      <button onClick={handleUpload} disabled={uploading || !files.length || !eventName}>
        {uploading ? "Uploading..." : "Upload Images"}
      </button>
    </div>
  );
};

export default AdminUploadPage;
